// frontend/packages/business-console/src/components/staff/ClientSwitcher.jsx
//
// Staff-only client picker rendered in the Console topbar. Lazy-loads
// /api/staff/businesses (cached for the session). For non-staff users
// this component renders nothing.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";

import { useAuth } from "../../contexts/AuthContext";
import { api } from "@upryzze/services";

const CACHE_KEY = "staffBusinessesCache";

export default function ClientSwitcher() {
  const { isStaff, businessId, setActiveBusinessId } = useAuth();
  const [options, setOptions] = useState(() => {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || "null");
      return Array.isArray(cached) ? cached : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (!isStaff) return;
    if (loadedRef.current) return;
    loadedRef.current = true;

    setLoading(true);
    api
      .get("/staff/businesses", { headers: { "x-business-id": "" } })
      .then((res) => {
        const list = Array.isArray(res?.data?.businesses) ? res.data.businesses : [];
        setOptions(list);
        try {
          sessionStorage.setItem(CACHE_KEY, JSON.stringify(list));
        } catch {
          // sessionStorage full or unavailable — caching is best-effort
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isStaff]);

  const selected = useMemo(
    () => options.find((b) => String(b._id) === String(businessId)) || null,
    [options, businessId],
  );

  if (!isStaff) return null;

  return (
    <Autocomplete
      size="small"
      options={options}
      value={selected}
      loading={loading}
      onChange={(_e, next) => {
        const nextId = next?._id ? String(next._id) : "";
        setActiveBusinessId(nextId);
        // Console workspace routes are slug-based (/business/:slug/*).
        // Navigate so the apiService also picks the tenant up via the
        // URL — sessionStorage is the fallback path.
        if (typeof window !== "undefined") {
          const dest = next?.slug ? `/business/${next.slug}/home` : "/";
          window.location.assign(dest);
        }
      }}
      getOptionLabel={(opt) => (opt?.name ? `${opt.name}` : "")}
      isOptionEqualToValue={(a, b) => String(a?._id) === String(b?._id)}
      sx={{ width: 280 }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={selected ? "" : "Select a client…"}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <Box sx={{ display: "flex", alignItems: "center", mr: 0.5, color: "text.secondary" }}>
                <BusinessOutlinedIcon fontSize="small" />
              </Box>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress size={14} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
